import queryString from 'query-string';
import { App, TaasInfo, isAppInList, areAppTypesEquivalent } from '@ps20-frontend/frontend-models';
import { Kooperation, Role, Zahlungsart, Zahlweise } from '@ps20/bff-models';
import { CommonInsuranceData, VersicherungsnehmerCommonInsuranceData } from './payload-models';

export const getAngebotsIdFromUrl = (hash: string): string => {
  const splitted = hash.split('?')[1];
  return queryString.parse(splitted)['angebotsId'] as string;
};

export function getZahlungsweise(actionPayload: CommonInsuranceData): string {
  if (actionPayload.vertragsoptionen.zahlungsweise) {
    return actionPayload.vertragsoptionen.zahlungsweise;
  }
  return Zahlweise.MONATLICH;
}

export function getZahlungsart(actionPayload: CommonInsuranceData): string {
  if (actionPayload.vertragsoptionen.zahlungsart) {
    return actionPayload.vertragsoptionen.zahlungsart;
  }
  return Zahlungsart.LASTSCHRIFT;
}

export function updateStateWithCommonInsuranceInfo<T extends PartialStateCommonInsuranceInfo>(
  actionPayload: CommonInsuranceData,
  state: T,
  appId: App,
): T {
  if (actionPayload.role === Role.SPS) {
    return {
      ...state,
      transientState: {
        ...state.transientState,
        role: actionPayload.role,
      },
    };
  }

  const risikoort = actionPayload.versicherungsnehmer.adresse;
  state = {
    ...state,
    vertragsoptionen: {
      ...state.vertragsoptionen,
      zahlungsweise: getZahlungsweise(actionPayload),
      zahlungsart: getZahlungsart(actionPayload),
      selectedKooperation: actionPayload.vertragsoptionen.selectedKooperation,
      angebotsdatum: state.vertragsoptionen?.angebotsdatum
        ? state.vertragsoptionen.angebotsdatum
        : actionPayload.vertragsoptionen.angebotsdatum,
    },
    vorbelegungen: {
      ...state.vorbelegungen,
      versicherungsnehmerVorbelegt: actionPayload.versicherungsnehmer.geburtsdatum !== null,
    },
    transientState: {
      ...state.transientState,
      role: actionPayload.role,
      kooperationen: actionPayload?.kooperationen,
    },
    taasInfo: {
      ...state.taasInfo,
      role: actionPayload.role,
    },
    versicherungsnehmer: {
      ...state.versicherungsnehmer,
      juristisch: actionPayload.versicherungsnehmer.juristisch,
      geburtsdatum: actionPayload.versicherungsnehmer.geburtsdatum,
      isAssekuranztarif: actionPayload.versicherungsnehmer.isAssekuranztarif,
      adresse: !isAppInList(appId, [App.TKV])
        ? actionPayload.versicherungsnehmer.adresse
        : undefined,
    },
  };

  if (actionPayload.vnAdresseSavedAsRisikoort) {
    if (!isAppInList(appId, [App.TKV])) {
      state = {
        ...state,
        transientState: {
          ...state.transientState,
          isRisikoortVnAdresse: true,
        },
      };
    }
    if (areAppTypesEquivalent(appId, App.VWG)) {
      state = {
        ...state,
        risikoort: {
          ...state.risikoort,
          plz: risikoort.plz,
          strasse: risikoort.strasse,
          ort: risikoort.ort,
          hausnummer: risikoort.hausnummer,
          hausnrzus: risikoort.hausnrzus,
        },
      };
    }
    if (areAppTypesEquivalent(appId, App.VHV)) {
      state = {
        ...state,
        risikoort: {
          ...state.risikoort,
          plz: risikoort.plz,
          strasse: risikoort.strasse,
          ort: risikoort.ort,
          hausnummer: risikoort.hausnummer,
        },
      };
    }
  }

  if (isAppInList(appId, [App.TKV])) {
    state = {
      ...state,
      vorbelegungen: {
        ...state.vorbelegungen,
        adresseImmobilieVorbelegt: actionPayload.versicherungsnehmer.geburtsdatum !== null,
      },
      risikoort: {
        ...state.risikoort,
        plz: risikoort.plz,
        strasse: risikoort.strasse,
        ort: risikoort.ort,
        hausnummer: risikoort.hausnummer,
      },
    };
  }

  state = {
    ...state,
    risikoort: {
      ...state.risikoort,
      invalidAddress: actionPayload.vnInvalidAddress,
    },
  };

  return state;
}

export interface PartialStateCommonInsuranceInfo {
  vertragsoptionen: {
    zahlungsweise: Zahlweise;
    zahlungsart: Zahlungsart;
    selectedKooperation?: string;
    angebotsdatum?: string;
  };
  vorbelegungen: {
    versicherungsnehmerVorbelegt: boolean;
  };
  transientState: {
    role: Role;
    isRisikoortVnAdresse?: boolean;
    koooperationen?: Kooperation[];
  };
  taasInfo?: TaasInfo;
  versicherungsnehmer: VersicherungsnehmerCommonInsuranceData;
  risikoort: {
    plz: string;
    ort: string;
    strasse: string;
    hausnummer: string;
    hausnrzus?: string;
    invalidAddress?: boolean;
  };
}
